/**
 * Form Validation Module
 * 
 * Provides client-side validation feedback for forms with:
 * - Real-time validation on blur and input events
 * - Email format validation
 * - Required field validation
 * - Inline error messages following design principles
 * - Accessibility support (ARIA attributes)
 * 
 * Usage:
 *   import { initFormValidation } from './form-validation.js';
 *   initFormValidation(document.querySelector('#ContactForm'));
 */

(function() {
  'use strict';

  // Validation rules
  const validators = {
    email: {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address'
    },
    required: {
      message: 'This field is required'
    },
    phone: {
      pattern: /^[\d\s\-\(\)]+$/,
      message: 'Please enter a valid phone number'
    }
  };

  /**
   * Get validation rules for a field
   */
  function getFieldRules(field) {
    const rules = [];
    
    // Check if required
    if (field.hasAttribute('required') || field.getAttribute('aria-required') === 'true') {
      rules.push('required');
    }
    
    // Check type-specific rules
    const type = field.type.toLowerCase();
    if (type === 'email') {
      rules.push('email');
    } else if (type === 'tel' || field.name.toLowerCase().includes('phone')) {
      rules.push('phone');
    }
    
    return rules;
  }

  /**
   * Validate a single field value
   */
  function validateField(field, value) {
    const rules = getFieldRules(field);
    const errors = [];
    
    for (const rule of rules) {
      if (rule === 'required' && !value.trim()) {
        errors.push(validators.required.message);
      } else if (rule === 'email' && value && !validators.email.pattern.test(value)) {
        errors.push(validators.email.message);
      } else if (rule === 'phone' && value && !validators.phone.pattern.test(value)) {
        errors.push(validators.phone.message);
      }
    }
    
    return errors;
  }

  /**
   * Create or get error message element for a field
   */
  function getOrCreateErrorMessage(field) {
    const fieldId = field.id || field.name;
    const errorId = `${fieldId}-validation-error`;
    
    // Check if error element already exists
    let errorElement = document.getElementById(errorId);
    if (errorElement) {
      return errorElement;
    }
    
    // Create error message element
    errorElement = document.createElement('small');
    errorElement.id = errorId;
    errorElement.className = 'field-error-message form__message';
    errorElement.setAttribute('role', 'alert');
    errorElement.setAttribute('aria-live', 'polite');
    errorElement.hidden = true;
    
    // Add icon wrapper
    const iconWrapper = document.createElement('span');
    iconWrapper.className = 'svg-wrapper';
    iconWrapper.innerHTML = `
      <svg class="icon icon-error" aria-hidden="true" focusable="false" viewBox="0 0 13 13">
        <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"/>
        <circle cx="6.5" cy="6.5" r="5.5" fill="var(--color-error, rgb(220, 38, 38))" fill-rule="evenodd"/>
        <path d="m5.87413 3.52832 1.25165-1.25165c.29289-.29289.76777-.29289 1.06066 0l1.25165 1.25165c.29289.29289.29289.76777 0 1.06066l-1.25165 1.25165c-.29289.29289-.76777.29289-1.06066 0l-1.25165-1.25165c-.29289-.29289-.29289-.76777 0-1.06066zm.5 3.5h1v4h-1v-4z" fill="white"/>
      </svg>
    `;
    
    const errorText = document.createElement('span');
    errorText.className = 'field-error-text';
    errorElement.appendChild(iconWrapper);
    errorElement.appendChild(errorText);
    
    // Insert after the field or its parent field container
    const fieldContainer = field.closest('.field') || field.parentElement;
    if (fieldContainer) {
      fieldContainer.appendChild(errorElement);
    } else {
      field.parentElement.insertBefore(errorElement, field.nextSibling);
    }
    
    return errorElement;
  }

  /**
   * Show validation error for a field
   */
  function showFieldError(field, message) {
    const errorElement = getOrCreateErrorMessage(field);
    const errorText = errorElement.querySelector('.field-error-text');
    
    if (errorText) {
      errorText.textContent = message;
    }
    
    errorElement.hidden = false;
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', errorElement.id);
    
    // Add error class to field container
    const fieldContainer = field.closest('.field');
    if (fieldContainer) {
      fieldContainer.classList.add('field--with-error');
    }
    
    // Add visual error state to input
    field.classList.add('field__input--error');
  }

  /**
   * Clear validation error for a field
   */
  function clearFieldError(field) {
    const fieldId = field.id || field.name;
    const errorId = `${fieldId}-validation-error`;
    const errorElement = document.getElementById(errorId);
    
    if (errorElement) {
      errorElement.hidden = true;
    }
    
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
    
    // Remove error class from field container
    const fieldContainer = field.closest('.field');
    if (fieldContainer) {
      fieldContainer.classList.remove('field--with-error');
    }
    
    // Remove visual error state from input
    field.classList.remove('field__input--error');
  }

  /**
   * Validate a field and show/clear errors
   */
  function handleFieldValidation(field, showErrors = true) {
    const value = field.value || '';
    const errors = validateField(field, value);
    
    if (errors.length > 0 && showErrors) {
      showFieldError(field, errors[0]); // Show first error
      return false;
    } else {
      clearFieldError(field);
      return true;
    }
  }

  /**
   * Validate entire form
   */
  function validateForm(form) {
    const fields = form.querySelectorAll('input[type="email"], input[type="text"], input[type="tel"], textarea, input[required], input[aria-required="true"]');
    let isValid = true;
    
    fields.forEach(field => {
      if (!handleFieldValidation(field, true)) {
        isValid = false;
      }
    });
    
    return isValid;
  }

  /**
   * Initialize form validation
   */
  function initFormValidation(form) {
    if (!form || form.dataset.validationInitialized) {
      return;
    }
    
    // Mark as initialized
    form.dataset.validationInitialized = 'true';
    
    // Get all validatable fields
    const fields = form.querySelectorAll('input[type="email"], input[type="text"], input[type="tel"], textarea, input[required], input[aria-required="true"]');
    
    // Add validation event listeners to each field
    fields.forEach(field => {
      // Validate on blur (after user leaves field)
      field.addEventListener('blur', function() {
        handleFieldValidation(field, true);
      });
      
      // Clear errors on input (real-time feedback)
      field.addEventListener('input', function() {
        if (field.classList.contains('field__input--error')) {
          handleFieldValidation(field, true);
        }
      });
    });
    
    // Validate on form submit
    form.addEventListener('submit', function(e) {
      if (!validateForm(form)) {
        e.preventDefault();
        
        // Track form validation failure
        if (window.ga4Analytics && window.ga4Analytics.isAvailable()) {
          const formName = form.id || form.name || form.action || 'unknown_form';
          window.ga4Analytics.trackFormSubmit(formName, false);
        }
        
        // Focus first invalid field
        const firstError = form.querySelector('.field__input--error, input[aria-invalid="true"]');
        if (firstError) {
          firstError.focus();
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        return false;
      } else {
        // Track successful form submission
        if (window.ga4Analytics && window.ga4Analytics.isAvailable()) {
          const formName = form.id || form.name || form.action || 'unknown_form';
          // Determine form type
          let formType = 'contact';
          if (form.id === 'ContactForm' || form.action?.includes('contact')) {
            formType = 'contact';
          } else if (form.classList.contains('newsletter-form') || form.action?.includes('newsletter')) {
            formType = 'newsletter';
          } else if (form.action?.includes('customers') || form.action?.includes('account')) {
            formType = 'account';
          }
          
          window.ga4Analytics.trackFormSubmit(formName, true);
          window.ga4Analytics.trackEvent('form_submit', {
            form_name: formName,
            form_type: formType,
            form_success: true
          });
        }
      }
    });
  }

  // Auto-initialize forms on DOM ready
  function autoInitForms() {
    // Initialize contact form
    const contactForm = document.querySelector('form#ContactForm');
    if (contactForm) initFormValidation(contactForm);
    
    // Initialize newsletter forms
    document.querySelectorAll('form.newsletter-form').forEach(form => {
      initFormValidation(form);
    });
    
    // Initialize registration form (create_customer or customers action)
    const registerForm = document.querySelector('form[action*="customers"], form[action*="account"]');
    if (registerForm) initFormValidation(registerForm);
    
    // Initialize any form with validation-ready fields (fallback)
    document.querySelectorAll('form').forEach(form => {
      if (!form.dataset.validationInitialized) {
        const hasValidatableFields = form.querySelectorAll('input[type="email"], input[required], input[aria-required="true"]').length > 0;
        if (hasValidatableFields) {
          initFormValidation(form);
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInitForms);
  } else {
    // DOM already loaded
    autoInitForms();
  }

  // Export for manual initialization
  if (typeof window !== 'undefined') {
    window.FormValidation = {
      init: initFormValidation,
      validate: validateForm,
      validateField: handleFieldValidation
    };
  }
})();

